import { useMemo, useState, useEffect, useRef } from 'react';
import { Bell, CheckCircle2, Circle, Clock, AlertTriangle, MessageSquare, Mail, Radio, Phone, ExternalLink, Trash2, Loader2, History, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { formatRelativeTime, formatTimeAgo } from '@/lib/utils/format';
import { Dialog, DialogContent, DialogClose, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { WhatsAppIcon } from '@/components/icons/whatsapp-icon';
import { InstagramIcon } from '@/components/icons/instagram-icon';
import { TelegramIcon } from '@/components/icons/telegram-icon';
import { useConversationStore } from '@/stores/conversation-store';
import { useUIStore } from '@/stores/ui-store';
import type { PastReminder } from '@/stores/conversation-store';
import type { Conversation, Channel } from '@/types/messaging';

const CHANNEL_ICONS: Record<Channel, React.ComponentType<{ className?: string }>> = {
  SMS: MessageSquare,
  MMS: MessageSquare,
  WHATSAPP: WhatsAppIcon,
  VOICE: Radio,
  EMAIL: Mail,
  INTERCOM: MessageCircle,
  INSTAGRAM: InstagramIcon,
  TELEGRAM: TelegramIcon,
};

const CHANNEL_LABELS: Record<Channel, string> = {
  SMS: 'SMS',
  MMS: 'MMS',
  WHATSAPP: 'WhatsApp',
  VOICE: 'Voice',
  EMAIL: 'Email',
  INTERCOM: 'Message',
  INSTAGRAM: 'Instagram',
  TELEGRAM: 'Telegram',
};

type TabKey = 'active' | 'past';

interface RemindersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenConversation: (conv: Conversation) => void;
}

export function RemindersDialog({ open, onOpenChange, onOpenConversation }: RemindersDialogProps) {
  const [tab, setTab] = useState<TabKey>('active');
  const conversations = useConversationStore((s) => s.conversations);
  const pastReminders = useConversationStore((s) => s.pastReminders);
  const pastRemindersLoading = useConversationStore((s) => s.pastRemindersLoading);
  const fetchPastReminders = useConversationStore((s) => s.fetchPastReminders);
  const activeReminders = useConversationStore((s) => s.activeReminders);
  const activeRemindersLoading = useConversationStore((s) => s.activeRemindersLoading);
  const activeRemindersFailed = useConversationStore((s) => s.activeRemindersFailed);
  const activeRemindersHasMore = useConversationStore((s) => s.activeRemindersHasMore);
  const activeRemindersLoadingMore = useConversationStore((s) => s.activeRemindersLoadingMore);
  const loadMoreActiveReminders = useConversationStore((s) => s.loadMoreActiveReminders);
  const pastRemindersHasMore = useConversationStore((s) => s.pastRemindersHasMore);
  const pastRemindersLoadingMore = useConversationStore((s) => s.pastRemindersLoadingMore);
  const loadMorePastReminders = useConversationStore((s) => s.loadMorePastReminders);
  const fetchActiveReminders = useConversationStore((s) => s.fetchActiveReminders);
  const cancelOrCompleteReminder = useConversationStore((s) => s.cancelOrCompleteReminder);

  // One pass over the conversations instead of `conversations.find(...)` inside
  // three render maps — 200 reminders × 500 chats walked the array 100k times per
  // render, and the drawer re-renders on every single-doc refresh-bus upsert.
  const convById = useMemo(() => {
    const m = new Map<string, Conversation>();
    for (const c of conversations) m.set(c.id, c);
    return m;
  }, [conversations]);

  /** Close a reminder out. The event id identifies which task to update. */
  const closeReminder = async (task: PastReminder, status: 'completed' | 'cancelled') => {
    // Awaited and checked: a task with no contactId (the store falls back to '')
    // makes this a silent no-op, so the row just sat there and the button looked
    // dead. SetReminderForm already reports the same failure.
    const ok = await cancelOrCompleteReminder(task.conversationId, task.contactId, status, task.eventId);
    if (!ok) {
      toast.error(
        status === 'completed' ? 'Could not mark it done' : 'Could not cancel the reminder',
        { description: 'It is still scheduled — please try again.' }
      );
    }
  };

  /** Jump to the reminder's conversation — loaded or not. The Active list is
   *  drawer-backed, so it can hold reminders on chats outside the current
   *  inbox page; those open via the chat dialog by id. */
  const openTaskConversation = (task: PastReminder) => {
    const conv = convById.get(task.conversationId);
    if (conv) onOpenConversation(conv);
    else if (task.conversationId) useUIStore.getState().openChatDialog(task.conversationId);
  };

  // Group: overdue, due soon (< 1h), upcoming
  const grouped = useMemo(() => {
    const now = Date.now();
    const overdue: PastReminder[] = [];
    const dueSoon: PastReminder[] = [];
    const upcoming: PastReminder[] = [];

    for (const t of activeReminders) {
      const diff = t.performAt - now;
      if (diff <= 0) overdue.push(t);
      else if (diff < 60 * 60 * 1000) dueSoon.push(t);
      else upcoming.push(t);
    }
    // fetchActiveReminders already sorts by performAt ascending
    return { overdue, dueSoon, upcoming };
  }, [activeReminders]);

  const activeCount = activeReminders.length;

  // Fetch the drawer once per visit to the Active tab (same guard pattern as
  // Past below — never key a refetch on the list being empty).
  const activeFetchedRef = useRef(false);
  useEffect(() => {
    if (!open || tab !== 'active') {
      activeFetchedRef.current = false;
      return;
    }
    if (activeFetchedRef.current) return;
    activeFetchedRef.current = true;
    fetchActiveReminders();
  }, [open, tab, fetchActiveReminders]);

  // Fetch past reminders once per visit to the Past tab.
  //
  // The guard has to be "have I already asked", not "is the list empty". Keying
  // off pastReminders.length re-armed the effect the moment an empty fetch
  // finished — and since pastRemindersLoading was a dependency, settling it back
  // to false re-ran the effect, which refetched, which settled again. That spun
  // until the Rate Guard blocked it and showed "Rate limited to your account".
  // It only bit when the result was empty, which was every time.
  const pastFetchedRef = useRef(false);
  useEffect(() => {
    if (!open || tab !== 'past') {
      pastFetchedRef.current = false; // re-arm for the next visit
      return;
    }
    if (pastFetchedRef.current) return;
    pastFetchedRef.current = true;
    fetchPastReminders();
  }, [open, tab, fetchPastReminders]);

  // Reset to active tab when dialog opens
  useEffect(() => {
    if (open) setTab('active');
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogClose onClose={() => onOpenChange(false)} />
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-amber-500" />
            Reminders
          </DialogTitle>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex items-center gap-1 mt-2 -mx-1">
          <button
            onClick={() => setTab('active')}
            className={cn(
              'flex-1 py-1.5 text-xs font-medium rounded-md transition-colors text-center',
              tab === 'active'
                ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            )}
          >
            {/* `10+` while another page exists — the lists are paged, and a bare
                count reads as a total, so an agent with forty pending reminders
                would see "Active (10)" and stop looking. */}
            Active{activeCount > 0 && ` (${activeCount}${activeRemindersHasMore ? '+' : ''})`}
          </button>
          <button
            onClick={() => setTab('past')}
            className={cn(
              'flex-1 py-1.5 text-xs font-medium rounded-md transition-colors text-center',
              tab === 'past'
                ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            )}
          >
            Past{pastReminders.length > 0 && ` (${pastReminders.length}${pastRemindersHasMore ? '+' : ''})`}
          </button>
        </div>

        <div className="mt-2 -mx-6 overflow-y-auto max-h-[60vh]">
          {tab === 'active' ? (
            /* ── Active reminders — the full drawer, not just chat badges ── */
            activeRemindersLoading && activeCount === 0 ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : activeRemindersFailed && activeCount === 0 ? (
              // Saying "no reminders" after a failed read is a lie the user acts
              // on — they set a duplicate. Say the list could not be loaded.
              <div className="flex flex-col items-center justify-center py-12 text-center px-6">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted mb-3">
                  <AlertTriangle className="h-7 w-7 text-amber-500/70" />
                </div>
                <p className="text-sm font-medium text-foreground">Couldn&apos;t load your reminders</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Existing reminders are still scheduled. Try again in a moment.
                </p>
                <button
                  onClick={() => fetchActiveReminders()}
                  className="mt-3 text-xs font-medium text-primary hover:underline"
                >
                  Retry
                </button>
              </div>
            ) : activeCount === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center px-6">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted mb-3">
                  <Bell className="h-7 w-7 text-muted-foreground/40" />
                </div>
                <p className="text-sm font-medium text-foreground">No active reminders</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Reminders you set on conversations will appear here.
                </p>
              </div>
            ) : (
              <div className="space-y-0">
                {grouped.overdue.length > 0 && (
                  <ReminderSection label="Overdue" count={grouped.overdue.length} variant="overdue">
                    {grouped.overdue.map((task) => (
                      <ReminderItem
                        key={task.eventId}
                        task={task}
                        conversation={convById.get(task.conversationId)}
                        variant="overdue"
                        onOpen={() => openTaskConversation(task)}
                        onMarkDone={() => closeReminder(task, 'completed')}
                        onDelete={() => closeReminder(task, 'cancelled')}
                      />
                    ))}
                  </ReminderSection>
                )}

                {grouped.dueSoon.length > 0 && (
                  <ReminderSection label="Due soon" count={grouped.dueSoon.length} variant="dueSoon">
                    {grouped.dueSoon.map((task) => (
                      <ReminderItem
                        key={task.eventId}
                        task={task}
                        conversation={convById.get(task.conversationId)}
                        variant="dueSoon"
                        onOpen={() => openTaskConversation(task)}
                        onMarkDone={() => closeReminder(task, 'completed')}
                        onDelete={() => closeReminder(task, 'cancelled')}
                      />
                    ))}
                  </ReminderSection>
                )}

                {grouped.upcoming.length > 0 && (
                  <ReminderSection label="Upcoming" count={grouped.upcoming.length} variant="upcoming">
                    {grouped.upcoming.map((task) => (
                      <ReminderItem
                        key={task.eventId}
                        task={task}
                        conversation={convById.get(task.conversationId)}
                        variant="upcoming"
                        onOpen={() => openTaskConversation(task)}
                        onMarkDone={() => closeReminder(task, 'completed')}
                        onDelete={() => closeReminder(task, 'cancelled')}
                      />
                    ))}
                  </ReminderSection>
                )}

                {activeRemindersHasMore && (
                  // The list is paged, the way the legacy client paged it — a bare
                  // cap would leave the rest unreachable.
                  <LoadMoreRow
                    loading={activeRemindersLoadingMore}
                    onClick={() => loadMoreActiveReminders()}
                    shown={activeReminders.length}
                  />
                )}
              </div>
            )
          ) : (
            /* ── Past reminders ────────────────────── */
            pastRemindersLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : pastReminders.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center px-6">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted mb-3">
                  <History className="h-7 w-7 text-muted-foreground/40" />
                </div>
                <p className="text-sm font-medium text-foreground">No past reminders</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Completed and cancelled reminders will appear here.
                </p>
              </div>
            ) : (
              <div className="space-y-0 px-4">
                {pastReminders.map((r) => (
                  <PastReminderItem key={r.eventId} reminder={r} />
                ))}
                {pastRemindersHasMore && (
                  <LoadMoreRow
                    loading={pastRemindersLoadingMore}
                    onClick={() => loadMorePastReminders()}
                    shown={pastReminders.length}
                  />
                )}
              </div>
            )
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ── Section header ─────────────────────────────────── */

function ReminderSection({
  label,
  count,
  variant,
  children,
}: {
  label: string;
  count: number;
  variant: 'overdue' | 'dueSoon' | 'upcoming';
  children: React.ReactNode;
}) {
  const colors = {
    overdue: 'text-red-600 dark:text-red-400',
    dueSoon: 'text-amber-600 dark:text-amber-400',
    upcoming: 'text-muted-foreground',
  };
  const icons = {
    overdue: <AlertTriangle className="h-3 w-3" />,
    dueSoon: <Clock className="h-3 w-3" />,
    upcoming: <Bell className="h-3 w-3" />,
  };

  return (
    <div className="pb-1">
      <div className={cn('flex items-center gap-1.5 px-6 py-2 text-[11px] font-semibold uppercase tracking-wider', colors[variant])}>
        {icons[variant]}
        {label}
        <span className="font-normal opacity-70">({count})</span>
      </div>
      <div>{children}</div>
    </div>
  );
}

/** "Load more" — one page at a time, so a long history stays reachable. */
function LoadMoreRow({ loading, onClick, shown }: { loading: boolean; onClick: () => void; shown: number }) {
  return (
    <div className="flex flex-col items-center gap-1 py-4">
      <button
        onClick={onClick}
        disabled={loading}
        className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50"
      >
        {loading && <Loader2 className="h-3 w-3 animate-spin" />}
        {loading ? 'Loading…' : 'Load more'}
      </button>
      <span className="text-[11px] text-muted-foreground/70">showing {shown}</span>
    </div>
  );
}

/* ── Active reminder item ───────────────────────── */

function ReminderItem({
  task,
  conversation,
  variant,
  onOpen,
  onMarkDone,
  onDelete,
}: {
  /** The reminder task itself — the source of truth for subject/times. */
  task: PastReminder;
  /** The chat it belongs to, when it happens to be in the loaded inbox list.
   *  Drawer-backed reminders can point at chats outside the current page. */
  conversation?: Conversation;
  variant: 'overdue' | 'dueSoon' | 'upcoming';
  onOpen: () => void;
  onMarkDone: () => void;
  onDelete: () => void;
}) {
  const ChannelIcon = (conversation && CHANNEL_ICONS[conversation.channel]) || Phone;

  const reminderTime = task.performAt;
  const reminderCreatedTime = task.createdTime || undefined;
  const reminderSubject = task.subject || 'Reminder';

  // Format due time as readable
  const dueLabel = useMemo(() => {
    if (!reminderTime) return '';
    const now = Date.now();
    const diff = reminderTime - now;
    if (diff <= 0) {
      return formatTimeAgo(reminderTime);
    }
    return formatRelativeTime(reminderTime);
  }, [reminderTime]);

  const borderColor = {
    overdue: 'border-l-red-500',
    dueSoon: 'border-l-amber-500',
    upcoming: 'border-l-blue-400 dark:border-l-blue-500',
  };

  return (
    <div
      className={cn(
        'group mx-4 mb-1.5 rounded-lg border border-border/60 bg-background hover:bg-accent/50 cursor-pointer transition-colors',
        'border-l-[3px]',
        borderColor[variant]
      )}
      onClick={onOpen}
      role="button"
      tabIndex={0}
    >
      <div className="px-3 py-2.5">
        {/* Row 1: Mark-done circle + Subject + Delete */}
        <div className="flex items-start gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); onMarkDone(); }}
            className="mt-0.5 shrink-0 group/check"
            title="Mark as done"
          >
            <Circle className="h-4 w-4 text-muted-foreground/40 group-hover/check:hidden" />
            <CheckCircle2 className="h-4 w-4 text-green-500 hidden group-hover/check:block" />
          </button>
          <span className="text-[13px] font-medium text-foreground leading-snug line-clamp-2 flex-1">
            {reminderSubject}
          </span>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="shrink-0 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-500 transition-all"
            title="Delete reminder"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Row 2: Due time + created time */}
        <div className="flex items-center gap-2 mt-1.5 pl-6">
          <Clock className="h-3 w-3 shrink-0 text-muted-foreground/60" />
          <span className={cn(
            'text-[11px] font-medium',
            variant === 'overdue' ? 'text-red-600 dark:text-red-400' :
            variant === 'dueSoon' ? 'text-amber-600 dark:text-amber-400' :
            'text-muted-foreground'
          )}>
            {variant === 'overdue' ? `Overdue \u00b7 ${dueLabel}` : `Due ${dueLabel}`}
          </span>
          {reminderCreatedTime && (
            <>
              <span className="text-muted-foreground/30">{'\u00b7'}</span>
              <span className="text-[11px] text-muted-foreground/60">
                Set {formatTimeAgo(reminderCreatedTime)}
              </span>
            </>
          )}
        </div>

        {/* Row 3: Conversation context chip */}
        <div className="flex items-center gap-1.5 mt-2 pl-6">
          <div
            className="inline-flex items-center gap-1.5 rounded-full bg-muted/60 px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-muted transition-colors"
            title="Open conversation"
          >
            <ChannelIcon className="h-3 w-3 shrink-0" />
            <span className="truncate max-w-[180px]">
              {conversation?.contactName || conversation?.contactPhone || task.contactId || task.conversationId || 'Unknown'}
            </span>
            <span className="text-muted-foreground/40">{'\u00b7'}</span>
            <span className="shrink-0">{conversation ? CHANNEL_LABELS[conversation.channel] : 'Open'}</span>
            <ExternalLink className="h-2.5 w-2.5 shrink-0 opacity-0 group-hover:opacity-60 transition-opacity" />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Past reminder item ───────────────────────── */

function PastReminderItem({ reminder }: { reminder: PastReminder }) {
  const dueLabel = useMemo(() => {
    if (!reminder.performAt) return '';
    return new Date(reminder.performAt).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  }, [reminder.performAt]);

  return (
    <div className="mb-1.5 rounded-lg border border-border/60 bg-background py-2.5 px-3">
      <div className="flex items-start gap-2">
        <CheckCircle2 className="h-4 w-4 text-green-500/60 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-medium text-foreground/70 line-clamp-1">{reminder.subject}</p>
          <div className="flex items-center gap-2 mt-1">
            <Clock className="h-3 w-3 text-muted-foreground/40 shrink-0" />
            <span className="text-[11px] text-muted-foreground/60">
              {reminder.status === 'completed' ? 'Completed' : 'Cancelled'}{' \u00b7 was due '}{dueLabel}
            </span>
          </div>
          {reminder.conversationId && (
            <div className="mt-1">
              <span className="text-[10px] text-muted-foreground/40 truncate block max-w-[240px]">
                {reminder.conversationId}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
